import { DistributorID } from './catalog';

/**
 * How a distributor's logo sits in a square mark tile.
 *
 * - `mark`: a compact symbol. It fits the square tile of the design.
 * - `wordmark`: the brand has only a wide word logo. It needs a wider plate,
 *   because a square tile shrinks the letters until nobody can read them.
 */
export type DistributorLogoShape = 'mark' | 'wordmark';

export interface DistributorLogo {
  /**
   * The file name in `src/music-services/assets/distributors/`. The file is the
   * brand's own artwork, unchanged, or a plain wordmark that we drew for a brand
   * with no artwork in the folder. The README in that folder lists where each
   * file comes from.
   */
  file: string;
  shape: DistributorLogoShape;
  /** Width divided by height of the artwork's `viewBox`. */
  aspectRatio: number;
  /**
   * The plate colour the brand puts this artwork on. It is the same in the
   * light and the dark theme: the artwork has one colour, so the plate keeps it
   * readable on both.
   */
  plate: string;
  /** True when the artwork paints its own background and fills the whole tile. */
  isFullBleed: boolean;
}

/**
 * The official logo of each distributor, for the Catalog connection cards.
 * `Record<DistributorID, …>` makes the compiler refuse a new distributor
 * without a logo entry.
 */
export const distributorLogos: Record<DistributorID, DistributorLogo> = {
  [DistributorID.DistroKid]: {
    file: 'distrokid-light.svg',
    shape: 'wordmark',
    aspectRatio: 515.17 / 66.38,
    plate: '#ffffff',
    isFullBleed: false,
  },
  [DistributorID.TuneCore]: {
    file: 'tunecore-dark.svg',
    shape: 'wordmark',
    aspectRatio: 306.7 / 87.7,
    plate: '#000000',
    isFullBleed: false,
  },
  [DistributorID.CDBaby]: {
    file: 'cdbaby-color.svg',
    shape: 'mark',
    aspectRatio: 84 / 55,
    plate: '#ffffff',
    isFullBleed: false,
  },
  [DistributorID.Ditto]: {
    file: 'ditto-dark.svg',
    shape: 'mark',
    aspectRatio: 1953.2 / 1000,
    plate: '#0a0a0a',
    isFullBleed: false,
  },
  [DistributorID.UnitedMasters]: {
    file: 'unitedmasters-on-background.svg',
    shape: 'mark',
    aspectRatio: 1,
    plate: '#181818',
    isFullBleed: true,
  },
  [DistributorID.ONErpm]: {
    file: 'onerpm-dark.svg',
    shape: 'wordmark',
    aspectRatio: 554.3 / 137.5,
    plate: '#000000',
    isFullBleed: false,
  },
  [DistributorID.TuneCoreJapan]: {
    file: 'tunecorejapan-dark.svg',
    shape: 'wordmark',
    aspectRatio: 983.9 / 110.8,
    plate: '#000000',
    isFullBleed: false,
  },
};

/** The logo of a distributor id, or `undefined` for any other integration type. */
export const getDistributorLogo = (integrationType: string): DistributorLogo | undefined =>
  (distributorLogos as Record<string, DistributorLogo | undefined>)[integrationType];
